import React, { useEffect, useState } from 'react';
import firebase from '../firebaseConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers,
    faUserPlus,
    faUser,
    faXmark,
    faCopy,
    faCircleCheck,
    faLock,
} from '@fortawesome/free-solid-svg-icons';
import User from '../models/User';
import { relativeTime } from '../utils';

const inviteStatus = (link) => {
    if (link.revoked) return { key: 'revoked', label: 'Revoked' };
    if (link['expires at'] && link['expires at'] < Date.now()) {
        return { key: 'expired', label: 'Expired' };
    }
    const max = link['max uses'];
    if (max && (link['redemption count'] || 0) >= max) {
        return { key: 'expired', label: 'Used up' };
    }
    return { key: 'active', label: 'Active' };
};

// Mirrors iOS ManageArtistAccessVC. Two sections: pending join requests
// (approve / reject) and active invite links (copy / revoke).
const ManageArtistAccess = ({ artist, onBack, onClose }) => {
    const [invites, setInvites] = useState(null);
    const [pending, setPending] = useState(null);
    const [profiles, setProfiles] = useState({});
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [copiedToken, setCopiedToken] = useState(null);

    const refreshInvites = async () => {
        try {
            const fn = firebase.functions().httpsCallable('listArtistInvites');
            const res = await fn({ artistId: artist.id });
            setInvites(res?.data?.links || []);
        } catch (err) {
            setError(err?.message || "Couldn't load invite links.");
            setInvites([]);
        }
    };

    const refreshPending = async () => {
        try {
            const snap = await firebase.database()
                .ref('artists').child(artist.id).child('pending invites').once('value');
            const raw = snap.val() || {};
            const list = Object.entries(raw).map(([uid, val]) => ({
                uid,
                requestedAt: val?.['requested at'] || null,
            }));
            list.sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0));
            setPending(list);
        } catch (err) {
            setError(err?.message || "Couldn't load join requests.");
            setPending([]);
        }
    };

    useEffect(() => {
        refreshInvites();
        refreshPending();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artist.id]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onBack(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onBack]);

    // Resolve requester uids to display names via the auth-readable
    // /users/{uid}/publicProfile subtree.
    useEffect(() => {
        if (!pending || pending.length === 0) return;
        let cancelled = false;
        pending.forEach(({ uid }) => {
            if (profiles[uid] !== undefined) return;
            User.getUserName(uid).then((name) => {
                if (!cancelled) setProfiles((prev) => ({ ...prev, [uid]: name || null }));
            }).catch(() => {
                if (!cancelled) setProfiles((prev) => ({ ...prev, [uid]: null }));
            });
        });
        return () => { cancelled = true; };
    }, [pending]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleApprove = async (uid) => {
        setBusyId(uid);
        setError('');
        try {
            await firebase.functions().httpsCallable('approveArtistJoinRequest')({
                artistId: artist.id, uId: uid,
            });
            await refreshPending();
        } catch (err) {
            setError(err?.message || "Couldn't approve the request.");
        } finally {
            setBusyId(null);
        }
    };

    const handleReject = async (uid) => {
        setBusyId(uid);
        setError('');
        try {
            await firebase.functions().httpsCallable('rejectArtistJoinRequest')({
                artistId: artist.id, uId: uid,
            });
            await refreshPending();
        } catch (err) {
            setError(err?.message || "Couldn't reject the request.");
        } finally {
            setBusyId(null);
        }
    };

    const handleRevoke = async (token) => {
        if (!window.confirm('Revoke this invite? Anyone holding the link can no longer join. People who already joined keep access.')) return;
        setBusyId(token);
        setError('');
        try {
            await firebase.functions().httpsCallable('revokeArtistInvite')({ token });
            await refreshInvites();
        } catch (err) {
            setError(err?.message || "Couldn't revoke the invite.");
        } finally {
            setBusyId(null);
        }
    };

    const handleCopy = async (token, url) => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedToken(token);
            setTimeout(() => setCopiedToken((cur) => (cur === token ? null : cur)), 1600);
        } catch (_e) { /* clipboard blocked */ }
    };

    const loading = invites === null || pending === null;
    const isEmpty = (pending?.length === 0) && (invites?.length === 0);

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-card share-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-eyebrow eyebrow-share">
                    <FontAwesomeIcon icon={faUsers} />
                    <span>LINKS &amp; REQUESTS</span>
                </div>
                <div className="modal-title">{artist.name}</div>

                {error && <div className="new-project-error">{error}</div>}

                {loading ? (
                    <div className="share-empty">Loading…</div>
                ) : isEmpty ? (
                    <div className="share-empty">No invite links or pending requests yet.</div>
                ) : (
                    <>
                        {pending.length > 0 && (
                            <>
                                <div className="share-section-title">PENDING REQUESTS</div>
                                <div className="share-link-list">
                                    {pending.map(({ uid, requestedAt }) => {
                                        const resolved = profiles[uid];
                                        const name = resolved
                                            || (resolved === undefined ? null : `Member ${uid.slice(0, 6)}`);
                                        return (
                                            <div key={uid} className="artist-pending-row">
                                                <span className="share-link-row-icon icon-blue">
                                                    <FontAwesomeIcon icon={faUser} />
                                                </span>
                                                <div className="share-link-row-main">
                                                    <div className="share-link-row-title">
                                                        {name ? name : (
                                                            <span className="skeleton-line viewer-name-skeleton" />
                                                        )}
                                                    </div>
                                                    {requestedAt && (
                                                        <div className="share-link-row-sub">
                                                            requested {relativeTime(requestedAt)} ago
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="share-link-row-actions">
                                                    <button
                                                        className="btn-primary"
                                                        disabled={busyId === uid}
                                                        onClick={() => handleApprove(uid)}
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        className="btn-ghost"
                                                        disabled={busyId === uid}
                                                        onClick={() => handleReject(uid)}
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {invites.length > 0 && (
                            <>
                                <div className="share-section-title">INVITE LINKS</div>
                                <div className="share-link-list">
                                    {invites.map((link) => {
                                        const status = inviteStatus(link);
                                        const isCopied = copiedToken === link.token;
                                        const uses = link['redemption count'] || 0;
                                        const max = link['max uses'];
                                        return (
                                            <div key={link.token} className="share-link-row">
                                                <span className="share-link-row-icon icon-purple">
                                                    <FontAwesomeIcon icon={faUserPlus} />
                                                </span>
                                                <div className="share-link-row-main">
                                                    <div className="share-link-row-title">
                                                        <span className="share-link-row-slug">{link.token}</span>
                                                        {link.hasPassword && (
                                                            <span className="share-permission-chip">
                                                                <FontAwesomeIcon icon={faLock} /> protected
                                                            </span>
                                                        )}
                                                        {link['requires approval'] && (
                                                            <span className="share-permission-chip">approval</span>
                                                        )}
                                                        <span className={`share-status-chip chip-${status.key}`}>
                                                            {status.label}
                                                        </span>
                                                    </div>
                                                    <div className="share-link-row-sub">
                                                        {uses} use{uses === 1 ? '' : 's'}
                                                        {max ? ` / ${max}` : ''}
                                                        {link['expires at']
                                                            ? ` · ${status.label === 'Expired'
                                                                ? 'expired'
                                                                : `expires ${new Date(link['expires at']).toLocaleDateString()}`}`
                                                            : ''}
                                                    </div>
                                                    <code className="share-link-row-url">{link.url}</code>
                                                </div>
                                                <div className="share-link-row-actions">
                                                    {status.key === 'active' && (
                                                        <button
                                                            className={`btn-ghost ${isCopied ? 'is-copied' : ''}`}
                                                            onClick={() => handleCopy(link.token, link.url)}
                                                        >
                                                            <FontAwesomeIcon icon={isCopied ? faCircleCheck : faCopy} />
                                                            {isCopied ? ' Copied' : ' Copy'}
                                                        </button>
                                                    )}
                                                    {!link.revoked && (
                                                        <button
                                                            className="btn-ghost"
                                                            disabled={busyId === link.token}
                                                            onClick={() => handleRevoke(link.token)}
                                                        >
                                                            <FontAwesomeIcon icon={faXmark} /> Revoke
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </>
                )}

                <div className="new-project-actions">
                    <button className="btn-ghost" onClick={onBack}>Back</button>
                    <button className="btn-primary" onClick={onClose}>Done</button>
                </div>
            </div>
        </div>
    );
};

export default ManageArtistAccess;
